/**
 * PayPal Secrets Service
 * 
 * Service pour interagir avec les endpoints de gestion des secrets PayPal.
 * Permet de sauvegarder et récupérer les métadonnées de configuration PayPal.
 */

import { app } from '@/shared/config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

// L'application est importée pour s'assurer que Firebase est initialisé si nécessaire,
// bien que nous utilisions fetch pour les appels API directs.
console.log('🔥 [PayPalSecretsService] Firebase initialized:', app.name);

export interface PayPalSecretsMetadata {
    hasClientId: boolean;
    hasClientSecret: boolean;
    hasWebhookId: boolean;
    webhookId: string | null;
    clientId: string | null;
    clientSecretMasked: string | null;
    isTestMode: boolean;
    configuredAt: string | null;
    webhookError?: string | null;
}

export interface SavePayPalSecretsRequest {
    organizationId: string;
    clientId: string;
    clientSecret: string;
    isTestMode: boolean;
}

export interface SavePayPalSecretsResponse {
    success: boolean;
    maskedClientId?: string;
    maskedClientSecret?: string;
    webhookId?: string;
    webhookError?: string;
    error?: string;
}

/**
 * Sauvegarde les secrets PayPal de manière sécurisée
 */
export async function savePayPalSecrets(request: SavePayPalSecretsRequest): Promise<SavePayPalSecretsResponse> {
    try {
        const response = await fetch(`${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL}/savePayPalSecrets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erreur lors de la sauvegarde des secrets PayPal');
        }

        return await response.json();
    } catch (error: any) {
        console.error('❌ [PayPalSecretsService] Error saving secrets:', error);
        throw error;
    }
}

/**
 * Récupère les métadonnées des secrets PayPal
 */
export async function getPayPalSecretsMetadata(organizationId: string): Promise<PayPalSecretsMetadata> {
    try {
        const response = await fetch(
            `${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL}/getPayPalSecretsMetadata?organizationId=${organizationId}`
        );

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erreur lors de la récupération des métadonnées PayPal');
        }

        return await response.json();
    } catch (error: any) {
        console.error('❌ [PayPalSecretsService] Error getting metadata:', error);
        throw error;
    }
}
export interface PayPalValidationResult {
    isValid: boolean;
    isTestMode: boolean;
    tests: {
        auth: { passed: boolean; message: string };
        orderCreation: { passed: boolean; message: string };
    };
    errors: string[];
    warnings: string[];
    scopes: {
        detected: string[];
        dangerous: string[];
        missing: string[];
    };
}

/**
 * Valide les secrets PayPal avant sauvegarde
 */
export async function validatePayPalSecrets(request: SavePayPalSecretsRequest): Promise<PayPalValidationResult> {
    try {
        const response = await fetch(`${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL}/validatePayPalSecrets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erreur lors de la validation des secrets PayPal');
        }

        return await response.json();
    } catch (error: any) {
        console.error('❌ [PayPalSecretsService] Error validating secrets:', error);
        return {
            isValid: false,
            isTestMode: request.isTestMode,
            tests: {
                auth: { passed: false, message: 'Erreur' },
                orderCreation: { passed: false, message: 'Erreur' },
            },
            errors: [error.message || 'Erreur lors de la validation'],
            warnings: [],
            scopes: { detected: [], dangerous: [], missing: [] }
        };
    }
}

/**
 * Valide les secrets PayPal déjà sauvegardés
 */
export async function validateSavedPayPalSecrets(organizationId: string): Promise<PayPalValidationResult> {
    try {
        const response = await fetch(`${import.meta.env.VITE_FIREBASE_FUNCTIONS_URL}/validateSavedPayPalSecrets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ organizationId }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Erreur lors de la validation des secrets PayPal sauvegardés');
        }

        return await response.json();
    } catch (error: any) {
        console.error('❌ [PayPalSecretsService] Error validating saved secrets:', error);
        return {
            isValid: false,
            isTestMode: false,
            tests: {
                auth: { passed: false, message: 'Erreur' },
                orderCreation: { passed: false, message: 'Erreur' },
            },
            errors: [error.message || 'Erreur lors de la validation'],
            warnings: [],
            scopes: { detected: [], dangerous: [], missing: [] }
        };
    }
}


